import { createVaultStore } from "./model/vaultStore.ts";
import { emptyDoc, type VaultTreeNode } from "./model/types.ts";
import { FileSystemVaultAdapter } from "./vault/FileSystemVaultAdapter.ts";
import {
  pickVault,
  restoreVault,
  requestVaultAccess,
  recoverVault,
  listVaults,
  forgetVault,
  touchVault,
  saveLastActivePath,
  savePinned,
  saveRecents,
  supportsFileSystemAccess,
  moveCamera,
  migrateLegacyTrash,
  type StoredVault,
} from "./vault/fsAccess.ts";
import { toJSONCanvas } from "./vault/jsonCanvas.ts";
import { retargetPath, retargetVaultRefs } from "./vault/canvasRefs.ts";
import { applyMarkdownRefFixes, planMarkdownRefFixes } from "./vault/mdRefs.ts";
import { isHiddenFolderPath } from "./vault/attachments.ts";
import { moveHighlights, HIGHLIGHTS_DIR } from "./vault/highlightStore.ts";
import { TRASH_DIR } from "./vault/trash.ts";
import { Workspace, type SlotId } from "./app/Workspace.ts";
import { Sidebar } from "./app/Sidebar.ts";
import { SearchPalette } from "./app/SearchPalette.ts";
import { CANVAS_SVG, DOC_SVG, FOLDER_BRAND_SVG } from "./app/glyphs.ts";
import { EdgeResizer } from "./app/EdgeResizer.ts";
import { openFolderDialog, openRenameDialog, confirmDialog, openLinkUpdateDialog, openNewDocDialog, openOptionsPopup, type OptionsPopupItem } from "./app/dialogs.ts";
import { openTrashDialog } from "./app/TrashDialog.ts";
import { installTooltips } from "./app/tooltip.ts";

function el<T extends HTMLElement = HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

/**
 * First document to show when a vault opens. Hidden folders are skipped: their
 * contents aren't listed in the sidebar, so opening one leaves the user staring
 * at a document they can't see, navigate away from, or find on disk.
 */
function firstFile(nodes: VaultTreeNode[]): string | null {
  for (const n of nodes) {
    if (n.children) {
      if (isHiddenFolderPath(n.path)) continue;
      const found = firstFile(n.children);
      if (found) return found;
    } else {
      return n.path;
    }
  }
  return null;
}

async function boot() {
  installTooltips();

  const vault = createVaultStore();

  // Tracks which vault (if any) is currently open, so a document switch can be
  // saved back to that vault's own "last view" — never the wrong vault's.
  let currentVaultId: string | null = null;
  vault.subscribe((state, prev) => {
    const path = state.activePath;
    if (!path || path === prev.activePath || !currentVaultId) return;
    void saveLastActivePath(currentVaultId, path);
    // Recientes es una pila sin repetidos: volver a algo lo sube al tope en vez
    // de dejar dos renglones diciendo lo mismo.
    //
    // Sólo lo alimenta el área grande. Consultar una nota al costado es
    // justamente el gesto de NO comprometerse con ella, el mismo motivo por el
    // que el panel no toca `activePath`.
    const next = [path, ...state.recents.filter((p) => p !== path)].slice(0, RECENTS_KEPT);
    vault.getState().setRecents(next);
    void saveRecents(currentVaultId, next);
  });

  const filesEl = el("sidebar-files");
  const treeEl = el("file-tree");

  /**
   * Cuántos recientes se GUARDAN. El rail muestra menos: tener de sobra es lo
   * que evita que borrar un archivo deje la lista corta.
   */
  const RECENTS_KEPT = 20;
  const emptyState = el("empty-state");
  const emptyStateText = el("empty-state-text");
  const docHost = el("doc-host");
  const welcome = el("welcome");
  const vaultList = el("vault-list");
  const welcomeHint = el("welcome-hint");

  const appEl = el("app");
  let sidebarSection: "files" | "search" = "files";

  function selectSidebarSection(section: typeof sidebarSection): void {
    sidebarSection = section;
    filesEl.hidden = section !== "files";
    el("sidebar-search").hidden = section !== "search";
    el("btn-search").setAttribute("aria-pressed", String(section === "search"));
    if (section !== "files") expandSidebar();
  }

  function setSidebarLayout(layout: "floating" | "docked"): void {
    appEl.classList.toggle("docked", layout === "docked");
  }

  function setViewMode(mode: "canvas" | "markdown" | null): void {
    appEl.classList.toggle("canvas-mode", mode === "canvas");
  }

  const peekEl = el("peek");
  const peekTitle = el("peek-title");

  /** Las flechas de ir y volver de cada lado. */
  const trailNav: Record<SlotId, { back: HTMLButtonElement; forward: HTMLButtonElement }> = {
    main: { back: el("btn-back"), forward: el("btn-forward") },
    peek: { back: el("btn-peek-back"), forward: el("btn-peek-forward") },
  };

  function setTrail(slot: SlotId, canBack: boolean, canForward: boolean): void {
    trailNav[slot].back.disabled = !canBack;
    trailNav[slot].forward.disabled = !canForward;
  }

  /**
   * Prender o apagar la barra de herramientas del documento del panel.
   *
   * El estado vive en el panel (una clase), no en la vista: el peek rehace su
   * MarkdownView en cada documento que se abre, así que guardarlo adentro se
   * perdería en cada salto. El CSS hace el resto.
   */
  const peekTools = el("btn-peek-tools");
  function setPeekTools(on: boolean): void {
    peekEl.classList.toggle("peek-tools", on);
    peekTools.setAttribute("aria-pressed", String(on));
    // El cartel de ayuda se lee del `title` (ver `tooltip.ts`), así que la
    // etiqueta se cambia ahí y no en `data-tip`.
    const label = on ? "Hide the document tools" : "Show the document tools";
    peekTools.title = label;
    peekTools.setAttribute("aria-label", label);
  }

  /** Mostrar el panel lateral titulado con `path`, o cerrarlo con `null`. */
  function setPeek(path: string | null): void {
    appEl.classList.toggle("peek-open", path !== null);
    peekEl.hidden = path === null;
    // Cerrar el panel lo deja como se abre: para leer. La próxima consulta
    // empieza otra vez con la hoja limpia.
    if (path === null) setPeekTools(false);
    peekTitle.textContent = path ? (path.split("/").pop() ?? path) : "";
    peekTitle.title = path ?? "";
    // El rail lo marca aparte de lo activo: misma pastilla, de contorno.
    vault.getState().setPeekPath(path);
  }

  const workspace = new Workspace(
    {
      host: docHost,
      peekHost: el("peek-host"),
      emptyState,
      emptyStateText,
      setSidebarLayout,
      setViewMode,
      setPeek,
      setTrail,
    },
    vault,
  );

  new Sidebar({ files: filesEl, pinned: el("pinned-group"), recents: el("recents-group"), tree: treeEl }, vault, {
    onOpen: (path, { peek }) =>
      void workspace.open(path, "", { slot: peek ? "peek" : "main" }),
    onOptions: (path, name, isFolder, x, y) => void onOptions(path, name, isFolder, x, y),
    onMove: (path, destDir) => void onMove(path, destDir),
  });

  // ---- welcome dashboard --------------------------------------------------

  function renderVaultRow(v: StoredVault): HTMLElement {
    const row = document.createElement("div");
    row.className = "vault-row";
    row.innerHTML =
      `<svg class="vault-row-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8">` +
      `<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>` +
      `<span class="vault-row-name"></span>` +
      `<button class="vault-row-remove" title="Remove from list">` +
      `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2">` +
      `<line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg></button>`;
    (row.querySelector(".vault-row-name") as HTMLElement).textContent = v.name;

    row.addEventListener("click", () => {
      void (async () => {
        if (await requestVaultAccess(v.handle)) {
          await touchVault(v.id);
          await openVault(v);
        } else {
          welcomeHint.textContent = `No permission for "${v.name}" — click again to retry.`;
        }
      })();
    });

    (row.querySelector(".vault-row-remove") as HTMLButtonElement).addEventListener(
      "click",
      (ev) => {
        ev.stopPropagation(); // don't open the vault we're removing
        void forgetVault(v.id).then(showWelcome);
      },
    );
    return row;
  }

  async function showWelcome(): Promise<void> {
    const vaults = await listVaults();
    if (vaults.length === 0) {
      const empty = document.createElement("p");
      empty.className = "vault-list-empty";
      empty.textContent = "You haven't opened any folder yet.";
      vaultList.replaceChildren(empty);
    } else {
      vaultList.replaceChildren(...vaults.map(renderVaultRow));
    }
    emptyState.hidden = true;
    welcome.hidden = false;
    appEl.classList.add("welcome-open");
  }

  function hideWelcome(): void {
    welcome.hidden = true;
    welcomeHint.textContent = "";
    appEl.classList.remove("welcome-open");
  }

  /** Close the open doc (flushing saves) and go back to the dashboard. */
  async function backToWelcome(): Promise<void> {
    await workspace.closeAll();
    search.reset();
    selectSidebarSection("files");
    vault.getState().setActive(null);
    currentVaultId = null;
    setSidebarLayout("floating");
    await showWelcome();
  }

  // ---- vault lifecycle ----------------------------------------------------

  async function openVault(v: StoredVault): Promise<void> {
    await workspace.closeAll();
    search.reset();
    selectSidebarSection("files");
    currentVaultId = v.id;
    const adapter = new FileSystemVaultAdapter(v.handle);
    vault.getState().setVault(adapter);
    vault.getState().setPinned(v.pinned ?? []);
    vault.getState().setRecents(v.recents ?? []);

    // Settle anything a previous session was cut off in the middle of, before
    // the tree is read — otherwise the first thing the user sees is a folder
    // that exists twice, or a half-copied one.
    let repairs: string[] = [];
    try {
      repairs = await recoverVault(v.handle);
    } catch {
      // A vault that can't be repaired still opens: the journal is a safety
      // net, and refusing to show the files because of it helps nobody.
    }

    // The trash used to be hidden inside `.notespace`. Bring anything still in
    // there out to the visible folder, once per vault.
    let movedFromHiddenTrash = 0;
    try {
      movedFromHiddenTrash = await migrateLegacyTrash(v.handle);
    } catch {
      // The old trash stays where it is and stays readable in Finder; a failed
      // migration is not a reason to refuse to open the vault.
    }

    // Empty folders the highlight mirror left behind on earlier runs. Cheap,
    // and it keeps `.notespace` from looking like a copy of the whole vault.
    try {
      await adapter.pruneEmptyDirs(HIGHLIGHTS_DIR);
    } catch {
      // Housekeeping only.
    }

    // Keep the dashboard covering everything while the tree loads and the first
    // document mounts. Hiding it up front revealed the shell in its in-between
    // state (floating sidebar, empty main), which then jumped to the docked
    // layout once the disk reads finished — the "two UIs" flicker.
    await vault.getState().refreshTree();

    // Resume the same document (md/canvas/pdf) the vault was left on, falling
    // back to the first file if it was deleted/moved since, or there is none yet.
    const wanted = v.lastActivePath;
    const wantedNode = wanted ? findNode(vault.getState().tree, wanted) : undefined;
    const target = wantedNode && !wantedNode.children ? wanted! : firstFile(vault.getState().tree);
    if (target) {
      await workspace.open(target);
    } else {
      emptyStateText.textContent = `"${vault.getState().name}" is empty — create your first note or canvas.`;
      emptyState.hidden = false;
      setSidebarLayout("docked");
      setViewMode(null);
    }

    hideWelcome();

    if (repairs.length > 0) {
      await confirmDialog({
        title: "Recovered an interrupted file operation",
        message: repairs.join(" "),
        okLabel: "Got it",
      });
    }

    if (movedFromHiddenTrash > 0) {
      await confirmDialog({
        title: "Your trash is now visible",
        message:
          `What you deleted was kept in a hidden folder only reachable from a terminal. ` +
          `${movedFromHiddenTrash} ${movedFromHiddenTrash === 1 ? "entry" : "entries"} moved to ` +
          `"${TRASH_DIR}" at the top of your vault, intact — open it in Finder, or use Trash in the sidebar to restore.`,
        okLabel: "Got it",
      });
    }
  }

  async function pickAndOpen(): Promise<void> {
    try {
      const v = await pickVault();
      await openVault(v);
    } catch (err) {
      // AbortError = user cancelled the picker; ignore.
      if ((err as Error)?.name !== "AbortError") console.error(err);
    }
  }

  // ---- file commands ------------------------------------------------------

  /** Prompt for the file name and create a new markdown note. */
  async function createNote(targetDir = ""): Promise<void> {
    const adapter = vault.getState().adapter;
    if (!adapter) return;
    const name = await openNewDocDialog("md");
    if (!name) return;
    const finalName = name.includes(".") ? name : `${name}.md`;
    const heading = finalName.replace(/\.[^.]+$/, "").replace(/^\w/, (c) => c.toUpperCase());
    const prefix = targetDir ? `${targetDir}/` : "";
    const path = await adapter.create(`${prefix}${finalName}`, `# ${heading}\n\n`);
    await vault.getState().refreshTree();
    await workspace.open(path);
  }

  /** Prompt for the file name and create a new canvas. */
  async function createCanvas(targetDir = ""): Promise<void> {
    const adapter = vault.getState().adapter;
    if (!adapter) return;
    const name = await openNewDocDialog("canvas");
    if (!name) return;
    const finalName = name.includes(".") ? name : `${name}.canvas`;
    const prefix = targetDir ? `${targetDir}/` : "";
    const path = await adapter.create(`${prefix}${finalName}`, toJSONCanvas(emptyDoc(finalName)));
    await vault.getState().refreshTree();
    await workspace.open(path);
  }

  /** Create a new folder. `targetDir`, if given, nests it inside that folder. */
  async function createFolderAction(targetDir = ""): Promise<void> {
    const adapter = vault.getState().adapter;
    if (!adapter) return;
    const name = await openFolderDialog();
    if (!name) return;
    const prefix = targetDir ? `${targetDir}/` : "";
    await adapter.createFolder(`${prefix}${name}`);
    await vault.getState().refreshTree();
  }

  function findNode(nodes: VaultTreeNode[], path: string): VaultTreeNode | undefined {
    for (const n of nodes) {
      if (n.path === path) return n;
      if (n.children) {
        const found = findNode(n.children, path);
        if (found) return found;
      }
    }
    return undefined;
  }

  /**
   * The entry already sitting at `to`, ignoring `from` itself.
   *
   * The comparison is case-insensitive because macOS (APFS) and Windows are:
   * there, "Notas" and "notas" name the same folder, so a rename that looks
   * like a free destination would actually land on top of an existing one.
   * Renaming an entry to a different capitalisation of *its own* name is legal
   * and handled by the adapter, hence the `from` exclusion.
   */
  function occupantAt(to: string, from: string): VaultTreeNode | undefined {
    const key = to.toLowerCase();
    const scan = (nodes: VaultTreeNode[]): VaultTreeNode | undefined => {
      for (const n of nodes) {
        if (n.path !== from && n.path.toLowerCase() === key) return n;
        if (n.children) {
          const found = scan(n.children);
          if (found) return found;
        }
      }
      return undefined;
    };
    return scan(vault.getState().tree);
  }

  /** Why `name` can't be used as a filename, or null if it can. A "/" is the
   *  dangerous one: it turns a rename into a move into another folder — and,
   *  for a folder, into a destination nested inside the entry being renamed. */
  function nameProblem(name: string): string | null {
    if (name === "" || name === "." || name === "..") return "That name is reserved.";
    if (/[\\/:*?"<>|]/.test(name)) return 'A name can\'t contain \\ / : * ? " < > or |.';
    return null;
  }

  /**
   * Los slots que `target` deja a medio camino: el que lo tenga abierto, y los
   * que tengan abierto algo adentro suyo si es una carpeta.
   *
   * Existe porque mover y borrar tocan el DISCO, y esa pregunta no es "¿es el
   * documento activo?" sino "¿lo tiene abierto alguna vista?". `suffix` es lo
   * que le queda al path por debajo de `target`, que es lo que hay que volver a
   * pegarle al destino para reabrir cada vista donde estaba.
   */
  function slotsUnder(target: string) {
    return workspace
      .openSlots()
      .filter(({ path }) => path === target || path.startsWith(`${target}/`))
      .map(({ slot, path }) => ({ slot, path, suffix: path.slice(target.length) }));
  }

  /**
   * Move/rename an entry on disk from `from` to `to`, keeping every open view
   * in sync (closing/reopening it, in its own slot, if it held the moved entry
   * or something nested inside a moved folder). Refuses to silently clobber an
   * existing entry at the destination.
   *
   * The file itself is only half the move: its highlights and saved viewport
   * are keyed by path, and every canvas embedding it stores that path too.
   * All of them are carried over here, or the move looks successful while
   * quietly losing annotations and breaking embeds.
   */
  async function movePath(from: string, to: string): Promise<void> {
    const adapter = vault.getState().adapter;
    if (!adapter || to === from) return;
    if (to.startsWith(`${from}/`)) {
      await confirmDialog({
        title: "A folder can't be moved inside itself",
        message: `"${from.split("/").pop()}" would end up nested in its own contents.`,
        okLabel: "Got it",
      });
      return;
    }
    const occupied = occupantAt(to, from);
    if (occupied) {
      await confirmDialog({
        title: "An item with that name already exists",
        message: `"${occupied.path.split("/").pop()}" already exists at the destination.`,
        okLabel: "Got it",
      });
      return;
    }

    const isFolder = !!findNode(vault.getState().tree, from)?.children;
    const affected = slotsUnder(from);
    for (const { slot } of affected) await workspace.close(slot);
    // Lo que esté a medio guardar se escribe ANTES de mover el archivo: un
    // autoguardado que llegue después escribiría en la ruta vieja, resucitando
    // el archivo que se acaba de mover.
    await workspace.flush();

    try {
      await adapter.rename(from, to);
    } catch (err) {
      // The adapter verifies the copy before removing anything, so a failure
      // here means nothing moved. Show the tree as it really is on disk and
      // reopen what was closed, instead of leaving the row (and the document)
      // missing and looking like the folder was swallowed.
      await vault.getState().refreshTree();
      for (const { slot, suffix } of affected) await workspace.open(from + suffix, "", { slot });
      await confirmDialog({
        title: `Couldn't rename "${from.split("/").pop()}"`,
        message: `${err instanceof Error ? err.message : String(err)} Nothing was moved — the files are still where they were.`,
        okLabel: "Got it",
      });
      return;
    }
    await moveHighlights(adapter, from, to, isFolder);
    await moveCamera(from, to);
    // El rastro guarda rutas: sin reapuntarlo, volver a un paso anterior iría a
    // buscar el archivo donde ya no está.
    workspace.retrail(from, to);
    retargetShortcuts(from, to);

    await vault.getState().refreshTree();
    for (const { slot, suffix } of affected) await workspace.open(to + suffix, "", { slot });

    // The open canvas is patched in memory; the rest are rewritten on disk.
    const openCanvas = workspace.retargetFileRefs(from, to);
    await retargetVaultRefs(adapter, vault.getState().tree, from, to, openCanvas);

    // Los enlaces de los `.md` también quedaron apuntando al lugar viejo, pero
    // a diferencia de un `.canvas` —que es formato de la app— acá se estaría
    // reescribiendo prosa del usuario, sin copia de respaldo. Se calcula el
    // plan, se muestra, y sólo se escribe si dice que sí.
    await updateMarkdownLinks(from, to);
  }

  /**
   * Ofrece reapuntar los enlaces de los `.md` después de mover `from` a `to`.
   *
   * El documento abierto se guarda antes de leer el vault: el barrido trabaja
   * sobre lo que hay en disco, y una edición sin volcar todavía haría que se
   * planifique sobre un texto viejo y después se lo pise con el corregido.
   */
  async function updateMarkdownLinks(from: string, to: string): Promise<void> {
    const adapter = vault.getState().adapter;
    if (!adapter) return;
    try {
      await workspace.flush();
      const fixes = await planMarkdownRefFixes(adapter, vault.getState().tree, from, to);
      if (fixes.length === 0) return;
      if (!(await openLinkUpdateDialog(fixes))) return;

      const failed = await applyMarkdownRefFixes(adapter, fixes);
      // El documento abierto puede ser uno de los reescritos: se recarga del
      // disco para que la vista no siga mostrando (y guardando) la versión de
      // antes, que volvería a dejar los enlaces rotos en el próximo autosave.
      for (const { slot, path } of workspace.openSlots()) {
        if (failed.includes(path) || !fixes.some((f) => f.path === path)) continue;
        // `reload`: es el mismo documento que ya está abierto, y abrir el
        // abierto no rehace nada. Acá justamente hace falta, porque el archivo
        // que la vista tiene en memoria quedó viejo.
        await workspace.open(path, "", { slot, reload: true });
      }
      if (failed.length > 0) {
        await confirmDialog({
          title: "Some links couldn't be updated",
          message: `${failed.length} document(s) couldn't be written: ${failed.join(", ")}.`,
          okLabel: "Got it",
        });
      }
    } catch (err) {
      // El archivo ya se movió: que falle el arreglo de los enlaces no puede
      // hacer parecer que falló el movimiento.
      console.error("link update failed", err);
    }
  }

  // ── Options drawer icons ────────────────────────────────────────────────
  // Markdown y canvas usan los mismos glifos que el árbol y el buscador: lo que
  // el menú promete crear tiene que ser lo que después aparece en la lista.
  const MD_ICON = DOC_SVG;
  const CANVAS_ICON = CANVAS_SVG;
  // Misma carpeta que el árbol, en azul de marca: las tres acciones de creación
  // son la misma familia, y la del menú tiene que ser la que después aparece en
  // la lista. La versión anterior tenía un "+" encima y era el único glifo del
  // grupo en gris.
  const FOLDER_ICON = FOLDER_BRAND_SVG;
  // El lápiz también va al azul de marca: el gris queda sólo para lo que no es
  // una acción del menú, y el rojo para la única destructiva.
  const EDIT_ICON =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="var(--ui-selection)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>';
  const PIN_ICON =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="var(--ui-selection)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M7 4h10v16.5l-5-3.6-5 3.6Z"/></svg>';
  const TRASH_ICON =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>';

  /** Las tres acciones de creación, compartidas por el "⋯" de una carpeta y el
   *  botón "Crear nuevo" del encabezado: es el mismo menú, y tiene que seguir
   *  siéndolo cuando se agregue un tipo de documento nuevo. */
  const CREATE_ITEMS: OptionsPopupItem[] = [
    { id: "new-md", label: "New markdown", icon: MD_ICON },
    { id: "new-canvas", label: "New canvas", icon: CANVAS_ICON },
    { id: "new-folder", label: "New folder", icon: FOLDER_ICON },
  ];

  /** Corre la acción de creación `action` dentro de `dir` ("" = raíz). */
  async function runCreate(action: string, dir: string): Promise<void> {
    if (action === "new-md") await createNote(dir);
    else if (action === "new-canvas") await createCanvas(dir);
    else if (action === "new-folder") await createFolderAction(dir);
  }

  /** Opens the popup for a file or folder and routes to the chosen action. */
  async function onOptions(
    path: string,
    name: string,
    isFolder: boolean,
    x: number,
    y: number,
  ): Promise<void> {
    const adapter = vault.getState().adapter;
    if (!adapter) return;

    const isRoot = path === "";
    const isPinned = vault.getState().pinned.includes(path);

    const items: OptionsPopupItem[] = isFolder
      ? [
          ...CREATE_ITEMS,
          ...(isRoot ? [] : [
            { id: "rename", label: "Rename", icon: EDIT_ICON },
            { id: "delete", label: "Delete", icon: TRASH_ICON, danger: true },
          ]),
        ]
      : [
          {
            id: isPinned ? "unpin" : "pin",
            label: isPinned ? "Unpin" : "Pin to sidebar",
            icon: PIN_ICON,
          },
          { id: "rename", label: "Rename", icon: EDIT_ICON },
          { id: "delete", label: "Delete", icon: TRASH_ICON, danger: true },
        ];

    const action = await openOptionsPopup(x, y, items);
    if (!action) return;

    if (action === "pin" || action === "unpin") togglePin(path);
    else if (action === "rename") await renameAction(path, name, isFolder);
    else if (action === "delete") await deleteAction(path, name, isFolder);
    else await runCreate(action, path);
  }

  /** Carpeta donde caen las cosas creadas desde el encabezado: la que contiene
   *  al documento abierto, o la raíz si no hay ninguno. Es la lectura más
   *  literal de "dónde estoy parado" que permite el modelo — el árbol sólo
   *  guarda selección de archivos, no de carpetas. */
  function currentDir(): string {
    const active = vault.getState().activePath;
    if (!active) return "";
    const cut = active.lastIndexOf("/");
    return cut === -1 ? "" : active.slice(0, cut);
  }

  /**
   * Fija o suelta un atajo. Lo nuevo va al final: el orden es el que se fue
   * armando, y reacomodarlo solo haría que el atajo de ayer esté hoy en otro
   * renglón, que es justo lo que un atajo no puede hacer.
   */
  function togglePin(path: string): void {
    if (!currentVaultId) return;
    const { pinned, setPinned } = vault.getState();
    const next = pinned.includes(path) ? pinned.filter((p) => p !== path) : [...pinned, path];
    setPinned(next);
    void savePinned(currentVaultId, next);
  }

  /** Guarda los atajos tal como quedaron en el store. */
  function persistShortcuts(pinned: string[], recents: string[]): void {
    if (!currentVaultId) return;
    vault.getState().setPinned(pinned);
    vault.getState().setRecents(recents);
    void savePinned(currentVaultId, pinned);
    void saveRecents(currentVaultId, recents);
  }

  /** Un archivo con atajos se movió: se reapuntan en vez de perderlos. */
  function retargetShortcuts(from: string, to: string): void {
    const { pinned, recents } = vault.getState();
    const move = (list: string[]): string[] => list.map((p) => retargetPath(p, from, to) ?? p);
    persistShortcuts(move(pinned), move(recents));
  }

  /** Se borró `path` (o la carpeta que lo contenía): se va también de los atajos. */
  function forgetShortcuts(path: string): void {
    const { pinned, recents } = vault.getState();
    const gone = (p: string): boolean => p === path || p.startsWith(`${path}/`);
    persistShortcuts(pinned.filter((p) => !gone(p)), recents.filter((p) => !gone(p)));
  }

  /** Rename via the rename dialog (preserves extension if omitted). */
  async function renameAction(path: string, name: string, isFolder: boolean): Promise<void> {
    const adapter = vault.getState().adapter;
    if (!adapter) return;
    const res = await openRenameDialog(name);
    if (!res) return;
    const trimmed = res.name;
    const problem = nameProblem(trimmed);
    if (problem) {
      await confirmDialog({ title: "That name can't be used", message: problem, okLabel: "Got it" });
      return;
    }
    // Folders have no extension: "notas.2024" is the whole name, and taking
    // ".2024" for one staples it back onto whatever the user types.
    const dot = isFolder ? -1 : name.lastIndexOf(".");
    const ext = dot > 0 ? name.slice(dot) : "";
    const finalName = trimmed.includes(".") || ext === "" ? trimmed : trimmed + ext;
    const dir = path.slice(0, path.length - name.length);
    await movePath(path, `${dir}${finalName}`);
  }

  /** Confirm and delete a file or folder (with confirmation). */
  async function deleteAction(path: string, name: string, isFolder: boolean): Promise<void> {
    const adapter = vault.getState().adapter;
    if (!adapter) return;
    const ok = await confirmDialog({
      title: `Delete "${name}"?`,
      message: isFolder
        ? `The folder and everything in it moves to the "${TRASH_DIR}" folder of your vault and stays there until you empty it.`
        : `It moves to the "${TRASH_DIR}" folder of your vault and stays there until you empty it.`,
      okLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    for (const { path: open } of slotsUnder(path)) await workspace.closeIfOpen(open);
    try {
      await adapter.remove(path);
    } catch (err) {
      // Nothing is unlinked until the copy into the trash is verified, so a
      // failure here leaves the entry where it was. Say so and show the tree
      // as it really is instead of leaving a row that looks half-deleted.
      await vault.getState().refreshTree();
      await confirmDialog({
        title: `Couldn't delete "${name}"`,
        message: `${err instanceof Error ? err.message : String(err)} Nothing was removed.`,
        okLabel: "Got it",
      });
      return;
    }
    forgetShortcuts(path);
    await vault.getState().refreshTree();
  }

  /** Drag-and-drop move: relocate `path` to be a child of `destDir` ("" = root). */
  async function onMove(path: string, destDir: string): Promise<void> {
    const base = path.split("/").pop()!;
    await movePath(path, destDir ? `${destDir}/${base}` : base);
  }

  // ---- wiring -------------------------------------------------------------

  // The brand card's home button returns to the dashboard (choose/manage vaults).
  el("btn-home").addEventListener("click", () => void backToWelcome());

  // Papelera: lo borrado, con Restaurar. La carpeta también está a la vista en
  // el vault, así que esto es el atajo, no el único camino.
  el("btn-trash").addEventListener("click", () => {
    const adapter = vault.getState().adapter;
    if (!adapter) return;
    void openTrashDialog(adapter, () => vault.getState().refreshTree());
  });

  // Buscador de archivos: botón del sidebar o ⌘K / Ctrl+K desde cualquier lado.
  const search = new SearchPalette(
    vault,
    (path, { peek }) => workspace.open(path, "", { slot: peek ? "peek" : "main" }),
    {
      host: el("sidebar-search"),
      onVisibility: (open) => {
        if (open) selectSidebarSection("search");
        else if (sidebarSection === "search") selectSidebarSection("files");
      },
    },
  );
  el("btn-search").addEventListener("click", () => search.open());
  window.addEventListener(
    "keydown",
    (e) => {
      if (e.key !== "k" || !(e.metaKey || e.ctrlKey) || e.altKey) return;
      e.preventDefault();
      // En captura y con stopPropagation porque el lienzo y CodeMirror también
      // escuchan teclas: el atajo tiene que ganar esté donde esté el foco.
      e.stopPropagation();
      search.toggle();
    },
    true,
  );

  // "Crear nuevo": mismo desplegable que el "⋯" de una carpeta, anclado bajo el
  // botón, apuntando a la carpeta del documento abierto.
  const btnNew = el("btn-new");
  btnNew.addEventListener("click", () => {
    void (async () => {
      if (!vault.getState().adapter) return;
      // Se ancla en la caja fantasma del botón, que replica la geometría del
      // "⋯" de una fila del árbol: así los dos menús —el mismo menú— abren en
      // el mismo punto de la pantalla, sin repetir acá las cuentas de padding.
      const r = (btnNew.querySelector(".menu-anchor") ?? btnNew).getBoundingClientRect();
      const action = await openOptionsPopup(r.left, r.bottom + 4, CREATE_ITEMS);
      if (action) await runCreate(action, currentDir());
    })();
  });
  el("btn-open-cta").addEventListener("click", () => void pickAndOpen());

  // ---- rastro: ir y volver ------------------------------------------------

  for (const slot of ["main", "peek"] as const) {
    trailNav[slot].back.addEventListener("click", () => void workspace.go(slot, -1));
    trailNav[slot].forward.addEventListener("click", () => void workspace.go(slot, 1));
  }

  // Botones 4 y 5 del mouse. En un navegador moverían el historial de la
  // pestaña —que acá es salirse de la app y perder el permiso del vault— así
  // que se atajan en `mousedown`, que es donde Chrome deja cancelarlos, y
  // mueven el rastro del lado en el que se está trabajando.
  window.addEventListener("mousedown", (e) => {
    if (e.button !== 3 && e.button !== 4) return;
    e.preventDefault();
    void workspace.go(workspace.focusedSlot, e.button === 3 ? -1 : 1);
  });

  // ---- panel lateral ------------------------------------------------------

  el("btn-peek-close").addEventListener("click", () => void workspace.close("peek"));

  peekTools.addEventListener("click", () =>
    setPeekTools(!peekEl.classList.contains("peek-tools")),
  );

  // Escape cierra el panel, pero sólo si nadie adentro lo usó primero: el
  // autocompletado, el buscador de `[[` y el menú `/` de la hoja lo consumen
  // para cerrarse, y ahí `defaultPrevented` ya viene marcado. Va en burbujeo
  // sobre el panel y no en captura sobre `window` justamente por eso — y así
  // Escape con el foco en el lienzo sigue siendo del lienzo.
  peekEl.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || e.defaultPrevented) return;
    void workspace.close("peek");
  });
  // Promover es la apertura de siempre en el área grande: la ocupación única
  // del Workspace se encarga de volcar y soltar el panel.
  el("btn-peek-full").addEventListener("click", () => {
    const path = workspace.peekPath;
    if (path) void workspace.open(path);
  });

  const peekResizer = new EdgeResizer({
    side: "left",
    cssVar: "--peek-width",
    storageKey: "blanqo.peek-width",
    defaultWidth: 420,
    minWidth: 320,
    maxWidth: 720,
    reserve: 480,
    onCollapse: () => void workspace.close("peek"),
  });
  peekEl.appendChild(peekResizer.el);

  // Quién atiende el teclado. Hace falta `pointerdown` en captura ADEMÁS de
  // `focusin` porque el lienzo hace `preventDefault` para no perder el foco al
  // clickearlo: mirándole sólo el foco, volver del panel al lienzo no le
  // devolvía nunca las teclas.
  for (const [slot, node] of [["main", docHost], ["peek", peekEl]] as const) {
    node.addEventListener("focusin", () => workspace.setFocusedSlot(slot));
    node.addEventListener("pointerdown", () => workspace.setFocusedSlot(slot), true);
  }

  // Collapse / reopen the sidebar (works in both floating and docked layouts).
  const btnReopen = el("btn-reopen");
  function collapseSidebar(): void {
    appEl.classList.add("sidebar-collapsed");
    btnReopen.hidden = false;
    btnReopen.focus({ preventScroll: true });
  }
  function expandSidebar(): void {
    appEl.classList.remove("sidebar-collapsed");
    btnReopen.hidden = true;
  }

  // Borde derecho arrastrable para ensanchar o angostar el panel.
  const sidebarEl = el("sidebar");
  // Native text selection and controls in the sidebar must not trigger Canvas actions.
  for (const type of ["keydown", "keyup", "copy", "cut", "paste"]) {
    sidebarEl.addEventListener(type, (event) => event.stopPropagation());
  }
  const sidebarResizer = new EdgeResizer({
      cssVar: "--sidebar-width",
      storageKey: "blanqo.sidebar-width",
      defaultWidth: 268,
      minWidth: 210,
      maxWidth: 460,
      reserve: 480,
      // Arrastrar el borde bien a la izquierda opaca el panel; sostenerlo (o
      // soltarlo ahí) lo cierra, como el botón de colapsar.
      onCollapse: collapseSidebar,
    });
  sidebarEl.appendChild(sidebarResizer.el);
  el("btn-collapse").addEventListener("click", collapseSidebar);
  btnReopen.addEventListener("click", expandSidebar);

  if (!supportsFileSystemAccess()) {
    el<HTMLButtonElement>("btn-open-cta").disabled = true;
    await showWelcome();
    welcomeHint.textContent =
      "Your browser doesn't support opening local folders. Use Chrome or Edge.";
    return;
  }

  // The dashboard is already visible (initial HTML state) so nothing else
  // flashes underneath; populate its vault list, then silently reopen the
  // last vault if the browser still has permission (openVault hides it).
  await showWelcome();
  const restored = await restoreVault();
  if (restored) await openVault(restored);
}

void boot();
